import { useCallback, useMemo } from 'react';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { NameType, Payload, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { BeautyPointsDashboardTileDetail, CollectedBeautyPointsAggregations, PaidOutBeautyPointsAggregations } from '~api/model/dashboardAggregations';

import { chartColors } from '~constants/colors';

import { formatDate, formatNumber, message } from '~helper/_common';

import { fetchCollectedBeautyPointsDashboardDetails, useCollectedBeautyPointsDashboardDetails } from '~modules/collectedBeautyPointsDashboardDetails';
import { fetchPaidOutBeautyPointsDashboardDetails, usePaidOutBeautyPointsDashboardDetails } from '~modules/paidOutBeautyPointsDashboardDetails';

import { Text } from '~components/atoms/Text';
import { chartSelectionProps, ChartTypeSelectionProps, DetailedChart, RenderChart, renderChartTypeSelection, WithOriginalValues } from '~components/organism/DetailedChart';
import { ComponentCommonProps } from '~components/types';

import style from './BeautyPointsDetailsTile.module.scss';

export type BeautyPointsDetailsTileProps = ComponentCommonProps & {
    creatorId?: string | null;
    mode: 'collectedBeautyPoints' | 'paidOutBeautyPoints';
    data: PaidOutBeautyPointsAggregations['paidOutBeautyPoints'] | CollectedBeautyPointsAggregations['collectedBeautyPoints'] | null;
};

const getTooltipLabel = (type: string) => {
    switch (type) {
        case 'liveViews':
        case 'deltaLiveViews':
            return message('common.liveViews');
        case 'replayViews':
        case 'deltaReplayViews':
            return message('common.replayViews');
        case 'behaviourPoints':
        case 'deltaBehaviourPoints':
            return message('common.behaviourPoints');
        default:
            return message('common.total');
    }
};

export const BeautyPointsDetailsTile: React.FC<BeautyPointsDetailsTileProps> = ({
    mode,
    data,
    creatorId,
    'data-test': dataTest = 'beautyPointsDetailsTile',
}) => {
    const collectedBeautyPointsDashboardDetails = useCollectedBeautyPointsDashboardDetails();
    const paidOutBeautyPointsDashboardDetails = usePaidOutBeautyPointsDashboardDetails();
    const dashboardDetails = mode === 'collectedBeautyPoints' ? collectedBeautyPointsDashboardDetails : paidOutBeautyPointsDashboardDetails;
    const chartData = dashboardDetails.dashboardDetails?.details || [];
    const chartProps: ChartTypeSelectionProps = useMemo(() => ({
        shows: {
            disabled: !chartData.some(value => value.liveViews),
            label: message('common.liveViews'),
        },
        clips: {
            disabled: !chartData.some(value => value.replayViews),
            label: message('common.replayViews'),
        },
        behaviourPoints: {
            disabled: !chartData.some(value => value.behaviourPoints),
            label: message('common.behaviourPoints'),
        },
    }), [ chartData ]);
    const renderChart = useCallback<RenderChart>((chartState, dataTest) => {
        const { shows, clips, behaviourPoints, total } = chartSelectionProps(chartState, chartProps);
        const noChartDataSelected = !chartState.shows && !chartState.clips && !chartState.behaviourPoints;

        const labelFormatter = (label: string, value: Payload<ValueType, NameType>[]) => {
            const date = value[0]?.payload?.date;

            return (
                <>
                    <Text variant="caption">
                        { `${message('dataTile.beautyPoints.headline')} ${message('dataTile.details.beautyPoints.collected')}` }
                    </Text>
                    <br />
                    <Text variant="caption">
                        { date && `${formatDate(date)}` }
                    </Text>
                </>
            );
        };

        return (
            <div className={ style.chartArea } data-test={ `${dataTest}.mainChart.${chartState.data}` }>
                { chartState.data === 'total' && (
                    <ResponsiveContainer>
                        <AreaChart data={ chartData } margin={ { top: 0, left: 0, right: 0, bottom: 0 } }>
                            <defs>
                                <linearGradient id="colorGradient_beautyPoints_total" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={ chartColors[mode] } stopOpacity={ 0.8 } />
                                    <stop offset="100%" stopColor={ chartColors[mode] } stopOpacity={ 0.05 } />
                                </linearGradient>
                                <linearGradient id="colorGradient_beautyPoints_shows" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={ chartColors.shows } stopOpacity={ 0.8 } />
                                    <stop offset="100%" stopColor={ chartColors.shows } stopOpacity={ 0.05 } />
                                </linearGradient>
                                <linearGradient id="colorGradient_beautyPoints_clips" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={ chartColors.clips } stopOpacity={ 0.8 } />
                                    <stop offset="100%" stopColor={ chartColors.clips } stopOpacity={ 0.05 } />
                                </linearGradient>
                                <linearGradient id="colorGradient_beautyPoints_behaviour" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={ chartColors.behaviourDrivenPoints } stopOpacity={ 0.8 } />
                                    <stop offset="100%" stopColor={ chartColors.behaviourDrivenPoints } stopOpacity={ 0.05 } />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" />
                            { total?.checked && (
                                <Area
                                    dataKey="total"
                                    type="monotone"
                                    stroke={ chartColors[mode] }
                                    fillOpacity={ noChartDataSelected ? 1 : 0 }
                                    fill="url(#colorGradient_beautyPoints_total)"
                                    data-test={ `${dataTest}.areaChart.total` } />
                            ) }
                            { shows?.checked && (
                                <Area
                                    dataKey="liveViews"
                                    type="monotone"
                                    stackId="a"
                                    stroke={ chartColors.shows }
                                    fillOpacity={ 1 }
                                    fill="url(#colorGradient_beautyPoints_shows)"
                                    data-test={ `${dataTest}.areaChart.shows` } />
                            ) }
                            { clips?.checked && (
                                <Area
                                    dataKey="replayViews"
                                    type="monotone"
                                    stackId="a"
                                    stroke={ chartColors.clips }
                                    fillOpacity={ 1 }
                                    fill="url(#colorGradient_beautyPoints_clips)"
                                    data-test={ `${dataTest}.areaChart.clips` } />
                            ) }
                            { behaviourPoints?.checked && (
                                <Area
                                    dataKey="behaviourPoints"
                                    type="monotone"
                                    stackId="a"
                                    stroke={ chartColors.behaviourDrivenPoints }
                                    fillOpacity={ 1 }
                                    fill="url(#colorGradient_beautyPoints_behaviour)"
                                    data-test={ `${dataTest}.areaChart.behaviourPoints` }
                                />
                            ) }
                            <XAxis
                                dataKey="date"
                                tickFormatter={ (value) => formatDate(value) } />
                            <YAxis
                                allowDecimals={ false }
                                tickFormatter={ (value) => formatNumber(value) } />
                            <Tooltip
                                formatter={ (value: number, name: keyof BeautyPointsDashboardTileDetail, data: { payload: WithOriginalValues<BeautyPointsDashboardTileDetail> }) => (
                                    [
                                        formatNumber(data.payload[name] as number), getTooltipLabel(name)
                                    ]
                                ) }
                                labelFormatter={ labelFormatter } />
                        </AreaChart>
                    </ResponsiveContainer>
                ) }
                { chartState.data === 'delta' && (
                    <ResponsiveContainer>
                        <BarChart barCategoryGap="10%" barGap="-80%" data={ chartData } margin={ { top: 0, left: 0, right: 0, bottom: 0 } }>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="date"
                                tickFormatter={ (value) => formatDate(value) } />
                            <YAxis
                                allowDecimals={ false }
                                tickFormatter={ (value) => formatNumber(value) } />
                            <Tooltip
                                formatter={ (value: number, name: keyof BeautyPointsDashboardTileDetail, data: { payload: WithOriginalValues<BeautyPointsDashboardTileDetail> }) => (
                                    [
                                        formatNumber(data.payload[name] as number), getTooltipLabel(name)
                                    ]
                                ) }
                                labelFormatter={ labelFormatter } />
                            { total?.checked && (
                                <Bar
                                    dataKey="deltaTotal"
                                    type="monotone"
                                    stroke={ chartColors[mode] }
                                    fillOpacity={ noChartDataSelected ? 1 : 0 }
                                    fill={ chartColors[mode] }
                                    data-test={ `${dataTest}.barChart.total` } />
                            ) }
                            { shows?.checked && (
                                <Bar
                                    dataKey="deltaLiveViews"
                                    type="monotone"
                                    stroke={ chartColors.shows }
                                    fillOpacity={ 1 }
                                    fill={ chartColors.shows }
                                    stackId="a"
                                    data-test={ `${dataTest}.barChart.shows` } />
                            ) }
                            { clips?.checked && (
                                <Bar
                                    dataKey="deltaReplayViews"
                                    type="monotone"
                                    stroke={ chartColors.clips }
                                    fillOpacity={ 1 }
                                    fill={ chartColors.clips }
                                    stackId="a"
                                    data-test={ `${dataTest}.barChart.clips` } />
                            ) }
                            { behaviourPoints?.checked && (
                                <Bar
                                    dataKey="deltaBehaviourPoints"
                                    type="monotone"
                                    stroke={ chartColors.behaviourDrivenPoints }
                                    fillOpacity={ 1 }
                                    fill={ chartColors.behaviourDrivenPoints }
                                    stackId="a"
                                    data-test={ `${dataTest}.barChart.behaviourPoints` }
                                />
                            ) }
                        </BarChart>
                    </ResponsiveContainer>
                ) }
            </div>
        );
    }, [ dashboardDetails, chartProps ]);

    return (
        <DetailedChart
            dataType={ mode }
            creatorId={ creatorId }
            headline={ message('dataTile.beautyPoints.headline') }
            label={ mode === 'collectedBeautyPoints' ? message('dataTile.details.beautyPoints.collected') : message('dataTile.details.beautyPoints.paidOut') }
            value={ formatNumber(data?.total || 0) }
            fetchChartData={ mode === 'collectedBeautyPoints' ? fetchCollectedBeautyPointsDashboardDetails : fetchPaidOutBeautyPointsDashboardDetails }
            fetchState={ dashboardDetails.fetchState }
            hasData={ Boolean(chartData.length) }
            renderChart={ renderChart }
            renderChartTypeSelection={ renderChartTypeSelection(chartProps) }
            data-test={ dataTest }
        />
    );
};
